import styled from "@emotion/styled";
import React, { useState } from 'react';
import { handleErrorMessage, i18n, showMessage, useGetOfferById } from 'src/common';
import { useRemoveVoucher } from 'src/common/api/apollo/mutations/useRemoveVoucher.mutation';
import { useValidateVoucher } from 'src/common/api/apollo/mutations/useValidateVoucher.mutation';
import { VoucherValidateResponseDto } from 'src/common/api/apollo/types/types';
import { colors } from 'src/theme';
import { Button } from '../buttons';
import { Flex } from '../grid';
import { InputText } from '../inputs/inputText.input';
import { Loader } from '../other';
import { Text } from '../typography';
import { Card } from './card';

const CardStyle = styled(Card)({
  borderWidth: 1,
  borderStyle: 'dashed',
  borderColor: colors.border3,

  '&:hover': {
    borderColor: colors.primary
  }
});

const StatusMessage = styled.div<{ isError?: boolean }>(({ isError }) => ({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.14',
  marginBottom: 0,
  paddingTop: '4px',
  transition: 'all 0.4s ease',
  color: isError ? colors.error1 : colors.success,
}));

const InputWrapper = styled.div({
  flex: 1
});

const InputTextStyle = styled(InputText)<{ hasAppliedCode?: boolean }>(({ hasAppliedCode }) => ({
  background: colors.background7,
  borderColor: hasAppliedCode ? colors.success : colors.border4,
  color: colors.text4,
  '&:disabled': {
    borderColor: hasAppliedCode ? colors.success : colors.border4
  }
}));

export interface VoucherCodeCardProps {
  offerId: string;
  appliedVoucherCode?: string | null;
}

export const VoucherCodeCard: React.FC<VoucherCodeCardProps> = ({ offerId, appliedVoucherCode }) => {
  const [voucherCode, setVoucherCode] = useState<string>(appliedVoucherCode || '');
  const [voucherResponse, setVoucherResponse] = useState<VoucherValidateResponseDto | null>(null);

  const { refetch } = useGetOfferById();
  const [validateVoucherMutation, { loading: validateLoading }] = useValidateVoucher();
  const [removeVoucherMutation, { loading: removeLoading }] = useRemoveVoucher();
  const isApplied = !!appliedVoucherCode || Boolean(voucherResponse?.voucherValue);
  const errorMessage = voucherResponse?.message || null;

  // Mutations reject on any GraphQL error (client errorPolicy 'none'); rate-limit denials
  // are already toasted by rateLimitErrorLink, anything else is shown here.
  const toastUnlessRateLimited = (error: unknown) => {
    const code = (error as { graphQLErrors?: { extensions?: { code?: unknown } }[] })?.graphQLErrors?.[0]?.extensions?.code;
    if (code !== 'RATE_LIMITED' && code !== 'IP_BLOCKED') {
      showMessage({ type: 'error', message: handleErrorMessage(error), duration: 5000, fixed: true });
    }
  };

  const handleSubmit = async () => {
    try {
      const { data } = await validateVoucherMutation({
        variables: { offerId, voucherCode },
      });
      if (data?.validateVoucher) {
        setVoucherResponse(data.validateVoucher);
        refetch();
      }
    } catch (error) {
      toastUnlessRateLimited(error);
    }
  };

  const handleRemove = async () => {
    try {
      const { data } = await removeVoucherMutation({
        variables: { offerId },
      });
      if (data?.removeVoucher) {
        setVoucherCode('');
        setVoucherResponse(null);
        refetch();
      }
    } catch (error) {
      toastUnlessRateLimited(error);
    }
  };

  if (validateLoading || removeLoading) return <Loader />;

  return (
    <CardStyle radius={12} padding={'20px 40px'} background='transparent'>
      <Flex vertical gap={12}>
        <Text type='base'>{i18n.t('components.cards.voucherCodeCard.title')}</Text>
        <Flex gap={12} align='start'>
          <InputWrapper>
            <InputTextStyle
              value={appliedVoucherCode || voucherCode}
              onChange={(value) => !isApplied && setVoucherCode(value)}
              disabled={isApplied}
              hasAppliedCode={isApplied}
            />
            {isApplied && (
              <StatusMessage>{i18n.t('components.cards.voucherCodeCard.successMessage')}</StatusMessage>
            )}
            {errorMessage && (
              <StatusMessage isError>{errorMessage}</StatusMessage>
            )}
          </InputWrapper>
          <Button
            disabled={!voucherCode}
            onClick={isApplied ? handleRemove : handleSubmit}
            btnType='outlinePrimary'
            text={i18n.t(`components.cards.voucherCodeCard.${isApplied ? 'removeButtonLabel' : 'buttonLabel'}`)}
          />
        </Flex>
      </Flex>
    </CardStyle>
  );
};
